import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Calendar, ChevronDown, ExternalLink, Lock, LockOpen, MessageSquare, Minus, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { HoverHintContent } from "@/components/ui/title-help";
import { ApiError, apiFetch, apiFetchBlob } from "@/lib/api";
import { useHubToast } from "@/components/ui/HubToast";
import { useAuth } from "@/components/AuthProvider";
import { useHubDevOverlayInset } from "@/lib/useHubDevOverlayInset";
import { WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL } from "@/lib/workspaceAvatarSync";
import {
  GOOGLE_WORKSPACE_SURFACES_KEY,
  parseGoogleWorkspaceSurfaces,
} from "@/lib/googleWorkspaceSurfaces";

interface GoogleChatStatus {
  eligible: boolean;
  oauthConfigured: boolean;
  connected: boolean;
  calendarConnected: boolean;
  sectionsConnected: boolean;
  readStateConnected: boolean;
  pubsubTopicConfigured: boolean;
  subscriptionName: string | null;
  subscriptionError: string | null;
}

interface CalendarEvent {
  id: string;
  summary: string;
  start: string | null;
  end: string | null;
  allDay: boolean;
  outOfOffice: boolean;
  hangoutLink: string | null;
  htmlLink: string | null;
}

interface PendingFile {
  filename: string;
  mimeType: string;
  dataBase64: string;
}

interface GoogleChatSpace {
  name: string;
  displayName: string;
  type: string;
  lastActiveTime: string | null;
  section: string | null;
  sectionSort: number | null;
  avatarUrl: string | null;
}

interface GoogleChatMessage {
  name: string;
  text: string;
  createTime: string | null;
  senderName: string;
  senderAvatarUrl: string | null;
  senderType: string;
  mine: boolean;
  threadName: string | null;
  reactions: Array<{ emoji: string; people: string[] }>;
  attachments: Array<{ name: string; contentType: string; contentName: string; previewUrl?: string }>;
}

function eventInstant(event: CalendarEvent): Date | null {
  if (event.start == null || event.start === "") return null;
  if (event.allDay && /^\d{4}-\d{2}-\d{2}$/.test(event.start)) {
    const [year, month, day] = event.start.split("-").map((part) => Number(part));
    return new Date(year, month - 1, day);
  }
  const date = new Date(event.start);
  return Number.isNaN(date.getTime()) ? null : date;
}

function chicagoDayKey(date: Date): string {
  return date.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
}

function eventDayLabel(event: CalendarEvent): string {
  const date = eventInstant(event);
  if (date == null) return "";
  const key = chicagoDayKey(date);
  const today = chicagoDayKey(new Date());
  const tomorrow = chicagoDayKey(new Date(Date.now() + 24 * 60 * 60 * 1000));
  if (key === today) return "Today";
  if (key === tomorrow) return "Tomorrow";
  return date.toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric" });
}

function eventClock(event: CalendarEvent): string {
  if (event.allDay) return "All day";
  const date = eventInstant(event);
  if (date == null) return "";
  return date.toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
}

function formatChatTime(iso: string | null): string {
  if (iso == null || iso === "") return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SenderAvatar({ name, url }: { name: string; url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (url == null || url === "" || failed) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-medium text-slate-700">
        {senderInitials(name)}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      referrerPolicy="no-referrer"
      className="h-8 w-8 shrink-0 rounded-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function senderInitials(name: string): string {
  const parts = name.split(/\s+/).filter((part) => part !== "");
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "");
  return letters.join("") || "?";
}

const SECTION_STORAGE_KEY = "hub-google-chat-sections";
const DOCK_STORAGE_KEY = "hub-google-chat-dock";
const LATER_STORAGE_KEY = "hub-google-chat-later";

interface LaterMessage {
  id: string;
  space: string;
  text: string;
  thread: string | null;
  sendAt: string;
}

function readDock(): Array<{ name: string; open: boolean }> {
  try {
    const raw = window.localStorage.getItem(DOCK_STORAGE_KEY);
    if (raw == null) return [];
    const parsed = JSON.parse(raw) as Array<{ name?: unknown; open?: unknown }>;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (typeof item?.name !== "string" || !item.name.startsWith("spaces/")) return [];
      return [{ name: item.name, open: item.open === true }];
    });
  } catch {
    return [];
  }
}

function readSections(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(SECTION_STORAGE_KEY);
    if (raw == null) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed != null && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readLater(): LaterMessage[] {
  try {
    const raw = window.localStorage.getItem(LATER_STORAGE_KEY);
    if (raw == null) return [];
    const parsed = JSON.parse(raw) as LaterMessage[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readPendingFile(file: File): Promise<PendingFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const dataBase64 = result.includes(",") ? (result.split(",")[1] ?? "") : result;
      resolve({
        filename: file.name || "attachment",
        mimeType: file.type || "application/octet-stream",
        dataBase64,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}

function spaceGroup(type: string): string {
  if (type === "DIRECT_MESSAGE") return "Direct messages";
  if (type === "GROUP_CHAT") return "Group chats";
  return "Spaces";
}

const BUILTIN_SECTIONS = ["Direct messages", "Spaces", "Group chats"];

function iconColor(name: string): string {
  const palette = ["#1a73e8", "#188038", "#e37400", "#9334e6", "#d93025", "#1967d2", "#137333", "#c5221f"];
  let hash = 0;
  for (const char of name) hash = (hash + char.charCodeAt(0)) % palette.length;
  return palette[hash] ?? "#1a73e8";
}

function ConversationIcon({ name, type, url }: { name: string; type: string; url?: string | null }) {
  const [failed, setFailed] = useState(false);
  const round = type === "DIRECT_MESSAGE" || type === "GROUP_CHAT" || type === "";
  if (url != null && url !== "" && !failed) {
    return (
      <img
        src={url}
        alt=""
        referrerPolicy="no-referrer"
        className={`h-8 w-8 shrink-0 object-cover ${round ? "rounded-full" : "rounded-md"}`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center text-xs font-medium text-white ${round ? "rounded-full" : "rounded-md"}`}
      style={{ backgroundColor: iconColor(name) }}
    >
      {senderInitials(name)}
    </span>
  );
}

function orderedSections(grouped: Map<string, GoogleChatSpace[]>): Array<[string, GoogleChatSpace[]]> {
  const entries = [...grouped.entries()].map(([group, rows]) => {
    const sorted = [...rows].sort((left, right) => left.displayName.localeCompare(right.displayName, "en"));
    return [group, sorted] as [string, GoogleChatSpace[]];
  });
  const custom = entries
    .filter(([group]) => !BUILTIN_SECTIONS.includes(group))
    .sort((left, right) => {
      const leftSort = left[1].find((row) => row.sectionSort != null)?.sectionSort ?? 0;
      const rightSort = right[1].find((row) => row.sectionSort != null)?.sectionSort ?? 0;
      if (leftSort !== rightSort) return leftSort - rightSort;
      return left[0].localeCompare(right[0], "en");
    });
  const builtin = BUILTIN_SECTIONS.flatMap((name) => {
    const match = entries.find(([group]) => group === name);
    return match == null ? [] : [match];
  });
  return [...custom, ...builtin];
}

async function fetchStatus(): Promise<GoogleChatStatus> {
  return apiFetch<GoogleChatStatus>("/api/google-chat/status");
}

export function GoogleChatDock() {
  const { session } = useAuth();
  const signedInEmail = (session?.user?.email ?? "").trim().toLowerCase();
  const eligible = signedInEmail !== "";
  const liftAboveQueuePill = signedInEmail === WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL;
  const surfaces = useQuery({
    queryKey: ["hub-ui-state", GOOGLE_WORKSPACE_SURFACES_KEY],
    queryFn: async () => {
      const response = await apiFetch<{ value: unknown }>(
        `/api/me/ui-state/${encodeURIComponent(GOOGLE_WORKSPACE_SURFACES_KEY)}`,
      );
      return parseGoogleWorkspaceSurfaces(response.value);
    },
    enabled: eligible,
    staleTime: 30_000,
  });
  const chatEnabled = surfaces.data?.chat === true;
  const calendarEnabled = surfaces.data?.calendar === true;
  const status = useQuery({
    queryKey: ["google-chat", "status"],
    queryFn: fetchStatus,
    enabled: eligible && (chatEnabled || calendarEnabled),
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const devInset = useHubDevOverlayInset();
  const { showToast } = useHubToast();
  const notifiedMeetings = useRef(new Set<string>());
  const dockRowRef = useRef<HTMLDivElement>(null);
  const [chatListOpen, setChatListOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarLocked, setCalendarLocked] = useState(true);
  const [calendarPos, setCalendarPos] = useState<{ x: number; y: number } | null>(null);
  const calendarDrag = useRef<{ dx: number; dy: number } | null>(null);
  const calendarRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState<Array<{ name: string; open: boolean }>>(() => readDock());
  const [unread, setUnread] = useState<Record<string, number>>({});
  const seenActivity = useRef<Record<string, string> | null>(null);
  const activeRef = useRef(active);
  const unreadRef = useRef(unread);
  activeRef.current = active;
  unreadRef.current = unread;
  const [sections, setSections] = useState<Record<string, string>>(() => readSections());

  useEffect(() => {
    window.localStorage.setItem(DOCK_STORAGE_KEY, JSON.stringify(active));
  }, [active]);
  const [sectionName, setSectionName] = useState("");
  const [threadQuery, setThreadQuery] = useState("");

  const spaces = useQuery({
    queryKey: ["google-chat", "spaces"],
    queryFn: () =>
      apiFetch<{ spaces: GoogleChatSpace[]; sectionsConnected: boolean; sectionError: string | null }>(
        "/api/google-chat/spaces",
      ),
    enabled: chatEnabled && status.data?.connected === true,
    refetchInterval: 12_000,
    retry: false,
  });

  useEffect(() => {
    const list = spaces.data?.spaces;
    if (list == null) return;
    if (seenActivity.current == null) {
      const primed: Record<string, string> = {};
      for (const space of list) primed[space.name] = space.lastActiveTime ?? "";
      seenActivity.current = primed;
      return;
    }
    const seen = seenActivity.current;
    for (const space of list) {
      const previous = seen[space.name] ?? "";
      const current = space.lastActiveTime ?? "";
      const open = activeRef.current.some((item) => item.name === space.name && item.open);
      const minimized = activeRef.current.some((item) => item.name === space.name && !item.open);
      const showingUnread = (unreadRef.current[space.name] ?? 0) > 0;
      if (current === "" || (current === previous && !showingUnread)) continue;
      if (open) {
        seen[space.name] = current;
        setUnread((counts) => (counts[space.name] == null ? counts : { ...counts, [space.name]: 0 }));
        continue;
      }
      if (!minimized && !showingUnread && current === previous) continue;
      const since = encodeURIComponent(previous);
      void apiFetch<{ unread: number }>(
        `/api/google-chat/unread?space=${encodeURIComponent(space.name)}&since=${since}`,
      ).then((result) => {
        if (result.unread <= 0) {
          seen[space.name] = current;
          setUnread((counts) => ({ ...counts, [space.name]: 0 }));
          return;
        }
        setUnread((counts) => ({ ...counts, [space.name]: result.unread }));
        setActive((currentActive) => {
          if (currentActive.some((item) => item.name === space.name)) return currentActive;
          return [...currentActive, { name: space.name, open: false }];
        });
      }).catch(() => {
        // The next space poll retries the count.
      });
    }
  }, [spaces.data]);

  const calendar = useQuery({
    queryKey: ["google-chat", "calendar"],
    queryFn: () => apiFetch<{ events: CalendarEvent[] }>("/api/google-chat/calendar"),
    enabled: calendarEnabled && status.data?.calendarConnected === true,
    refetchInterval: 60_000,
    retry: false,
  });

  const calendarEvents = useRef<CalendarEvent[]>([]);
  calendarEvents.current = calendar.data?.events ?? [];
  useEffect(() => {
    const remind = () => {
      for (const event of calendarEvents.current) {
        if (event.allDay || event.outOfOffice || event.start == null || notifiedMeetings.current.has(event.id)) continue;
        const startsIn = Date.parse(event.start) - Date.now();
        if (Number.isNaN(startsIn) || startsIn <= -2 * 60 * 1000 || startsIn > 10 * 60 * 1000) continue;
        notifiedMeetings.current.add(event.id);
        const message = startsIn <= 60_000 ? `${event.summary} is starting now.` : `${event.summary} starts in ${Math.max(1, Math.round(startsIn / 60_000))} minutes.`;
        showToast({ tone: "info", message });
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification(event.summary, { body: message });
        }
      }
    };
    remind();
    const timer = window.setInterval(remind, 30_000);
    return () => window.clearInterval(timer);
  }, [showToast]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const due = readLater().filter((item) => Date.parse(item.sendAt) <= Date.now());
      if (due.length === 0) return;
      const remaining = readLater().filter((item) => Date.parse(item.sendAt) > Date.now());
      window.localStorage.setItem(LATER_STORAGE_KEY, JSON.stringify(remaining));
      for (const item of due) {
        void apiFetch("/api/google-chat/messages", {
          method: "POST",
          body: { space: item.space, text: item.text, thread: item.thread },
        });
      }
    }, 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const expandedCount = active.filter((item) => item.open).length;
  useLayoutEffect(() => {
    const row = dockRowRef.current;
    if (row == null) return;
    const collapseOverflow = () => {
      if (row.getBoundingClientRect().left >= 8) return;
      const pushed = [...row.children].find((child) => {
        if (!(child instanceof HTMLElement)) return false;
        if (getComputedStyle(child).position === "fixed") return false;
        const spaceName = child.dataset.chatSpace;
        return spaceName != null && spaceName !== "" && child.getBoundingClientRect().left < 8;
      });
      const spaceName = pushed instanceof HTMLElement ? pushed.dataset.chatSpace : undefined;
      if (spaceName == null || spaceName === "") return;
      setActive((current) => current.map((entry) => (entry.name === spaceName ? { ...entry, open: false } : entry)));
    };
    collapseOverflow();
    window.addEventListener("resize", collapseOverflow);
    return () => window.removeEventListener("resize", collapseOverflow);
  }, [expandedCount, calendarOpen]);

  if (!eligible || (!chatEnabled && !calendarEnabled)) return null;

  const bottom = liftAboveQueuePill ? Math.max(12, devInset.clearancePx) : 12;
  const byName = new Map((spaces.data?.spaces ?? []).map((space) => [space.name, space]));
  const filtered = (spaces.data?.spaces ?? []).filter((space) =>
    space.displayName.toLowerCase().includes(threadQuery.trim().toLowerCase()),
  );
  const grouped = new Map<string, GoogleChatSpace[]>();
  for (const space of filtered) {
    const group = space.section?.trim() || sections[space.name]?.trim() || spaceGroup(space.type);
    const list = grouped.get(group) ?? [];
    list.push(space);
    grouped.set(group, list);
  }

  function markRead(name: string): void {
    const seen = seenActivity.current;
    const space = byName.get(name);
    if (seen != null) seen[name] = space?.lastActiveTime ?? new Date().toISOString();
    setUnread((counts) => ({ ...counts, [name]: 0 }));
    void apiFetch("/api/google-chat/read", { method: "POST", body: { space: name } }).catch(() => {
      // The chat list shows Allow read state when Google has not granted updates.
    });
  }

  function openSpace(name: string): void {
    markRead(name);
    setActive((current) => {
      const existing = current.find((item) => item.name === name);
      if (existing != null) {
        return current.map((item) => (item.name === name ? { ...item, open: true } : item));
      }
      return [...current, { name, open: true }];
    });
    setChatListOpen(false);
  }

  function assignSection(spaceName: string): void {
    const nextName = sectionName.trim();
    if (nextName === "") return;
    const next = { ...sections, [spaceName]: nextName };
    setSections(next);
    window.localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next));
  }

  const minimized = active.filter((item) => !item.open);
  const expanded = active.filter((item) => item.open);

  return (
    <div className="pointer-events-none fixed z-[90] flex items-end gap-2" style={{ right: 16, bottom }}>
      <div ref={dockRowRef} className="pointer-events-auto flex items-end gap-2">
        {chatEnabled ? expanded.map((item) => (
          <ChatWindow
            key={item.name}
            data-chat-space={item.name}
            space={byName.get(item.name) ?? { name: item.name, displayName: item.name, type: "", lastActiveTime: null, section: null, sectionSort: null, avatarUrl: null }}
            onCollapse={() => setActive((current) => current.map((entry) => entry.name === item.name ? { ...entry, open: false } : entry))}
            onClose={() => setActive((current) => current.filter((entry) => entry.name !== item.name))}
          />
        )) : null}
        {calendarEnabled && calendarOpen ? (
          <section
            ref={calendarRef}
            className="flex max-h-[28rem] w-96 shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
            style={calendarPos != null ? { position: "fixed", left: calendarPos.x, top: calendarPos.y, zIndex: 95 } : undefined}
          >
            <header
              className={`flex items-center justify-between border-b border-slate-200 px-3 py-2 ${calendarLocked ? "" : "cursor-move"}`}
              onPointerDown={(event) => {
                if (calendarLocked || calendarRef.current == null) return;
                const target = event.target as HTMLElement;
                if (target.closest("button")) return;
                const rect = calendarRef.current.getBoundingClientRect();
                calendarDrag.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                if (calendarDrag.current == null) return;
                setCalendarPos({
                  x: event.clientX - calendarDrag.current.dx,
                  y: event.clientY - calendarDrag.current.dy,
                });
              }}
              onPointerUp={() => {
                calendarDrag.current = null;
              }}
            >
              <h2 className="text-sm font-semibold text-slate-900">Calendar</h2>
              <div className="flex items-center gap-1 text-slate-600">
              <a
                href="https://calendar.google.com/calendar/r"
                target="_blank"
                rel="noreferrer"
                aria-label="Open Google Calendar"
                className="rounded p-1 hover:bg-slate-100"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                type="button"
                aria-label={calendarLocked ? "Unlock to drag" : "Lock in the dock"}
                className="rounded p-1 text-slate-600 hover:bg-slate-100"
                onClick={() => {
                  if (calendarLocked && calendarRef.current != null) {
                    const rect = calendarRef.current.getBoundingClientRect();
                    setCalendarPos({ x: rect.left, y: rect.top });
                    setCalendarLocked(false);
                    return;
                  }
                  setCalendarLocked(true);
                  setCalendarPos(null);
                }}
              >
                {calendarLocked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
              </button>
              </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto text-sm text-slate-800">
              {status.data?.calendarConnected !== true ? (
                <ConnectChat label="Allow calendar" />
              ) : calendar.isError ? (
                <p className="px-3 py-2 text-xs text-red-700">{calendar.error instanceof ApiError ? calendar.error.message : "Calendar did not load."}</p>
              ) : (calendar.data?.events.length ?? 0) === 0 ? (
                <p className="px-3 py-2 text-xs text-slate-500">{calendar.isLoading ? "Loading meetings..." : "No meetings in the next 7 days."}</p>
              ) : (
                <CalendarAgenda events={calendar.data?.events ?? []} />
              )}
            </div>
          </section>
        ) : null}
      </div>
      <div className="pointer-events-auto relative flex flex-col-reverse items-end gap-2">
        <div className="relative">
        {chatEnabled && chatListOpen ? (
          <section className="absolute bottom-14 right-0 z-[91] flex h-[28rem] w-96 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            <header className="border-b border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900">Google Chat</header>
            {status.data?.connected !== true ? (
              <ConnectChat />
            ) : (
              <>
                <input
                  className="m-2 rounded-md border border-slate-300 px-2 py-1 text-sm"
                  value={threadQuery}
                  onChange={(event) => setThreadQuery(event.target.value)}
                  placeholder="Find a conversation"
                  aria-label="Find a conversation"
                />
                {status.data?.readStateConnected === true ? null : (
                  <div className="mx-2 mb-2">
                    <ConnectChat label="Allow read state" />
                  </div>
                )}
                {status.data?.sectionsConnected === true ? null : (
                  <div className="mx-2 mb-2 space-y-2">
                    <ConnectChat label="Allow chat sections" />
                    <input
                      className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                      value={sectionName}
                      onChange={(event) => setSectionName(event.target.value)}
                      placeholder="Custom section name"
                      aria-label="Custom section name"
                    />
                  </div>
                )}
                {spaces.data?.sectionError != null && spaces.data.sectionError !== "" ? (
                  <p className="mx-2 mb-2 text-xs text-red-700">{spaces.data.sectionError}</p>
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                  {orderedSections(grouped).map(([group, rows]) => (
                    <div key={group} className="mb-3">
                      <p className="px-1 py-1 text-xs font-semibold text-slate-500">{group}</p>
                      {rows.map((space) => (
                        <div key={space.name} className="flex items-center gap-1">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1 text-left text-sm text-slate-800 hover:bg-slate-100"
                            onClick={() => openSpace(space.name)}
                          >
                            <ConversationIcon name={space.displayName} type={space.type} url={space.avatarUrl} />
                            <span className="min-w-0 truncate">{space.displayName}</span>
                          </button>
                          {status.data?.sectionsConnected === true ? null : (
                            <button
                              type="button"
                              className="shrink-0 px-1 text-xs text-slate-500"
                              onClick={() => assignSection(space.name)}
                            >
                              Section
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        ) : null}
        <div className="flex h-12 overflow-hidden rounded-full bg-brand-600 text-white shadow-lg">
          {chatEnabled ? (
            <button
              type="button"
              aria-label="Chat"
              className="px-4 hover:bg-brand-700"
              onClick={() => setChatListOpen((current) => !current)}
            >
              <MessageSquare className="h-5 w-5" />
            </button>
          ) : null}
          {chatEnabled && calendarEnabled ? <span className="w-px self-stretch bg-white/30" /> : null}
          {calendarEnabled ? (
            <button
              type="button"
              aria-label="Calendar"
              className="px-4 hover:bg-brand-700"
              onClick={() => {
                if (typeof Notification !== "undefined" && Notification.permission === "default") {
                  void Notification.requestPermission();
                }
                setCalendarOpen((current) => !current);
              }}
            >
              <Calendar className="h-5 w-5" />
            </button>
          ) : null}
        </div>
        </div>
        {chatEnabled ? minimized.map((item) => {
          const space = byName.get(item.name);
          const label = space?.displayName ?? "Chat";
          return (
            <button
              key={item.name}
              type="button"
              title={label}
              className="group relative"
              onClick={() => {
                markRead(item.name);
                setActive((current) => current.map((entry) => entry.name === item.name ? { ...entry, open: true } : entry));
              }}
            >
              <ConversationIcon name={label} type={space?.type ?? "DIRECT_MESSAGE"} url={space?.avatarUrl} />
              {(unread[item.name] ?? 0) > 0 ? (
                <span className="absolute -left-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] text-white">
                  {(unread[item.name] ?? 0) > 9 ? "9+" : unread[item.name]}
                </span>
              ) : null}
              <span
                role="button"
                aria-label="Close"
                className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-slate-700 text-white group-hover:flex"
                onClick={(event) => {
                  event.stopPropagation();
                  setActive((current) => current.filter((entry) => entry.name !== item.name));
                }}
              >
                <X className="h-3 w-3" />
              </span>
            </button>
          );
        }) : null}
      </div>
    </div>
  );
}

function CalendarAgenda({ events }: { events: CalendarEvent[] }) {
  const groups: Array<{ label: string; events: CalendarEvent[] }> = [];
  for (const event of events) {
    const label = eventDayLabel(event);
    const current = groups[groups.length - 1];
    if (current != null && current.label === label) current.events.push(event);
    else groups.push({ label, events: [event] });
  }
  return (
    <div>
      {groups.map((group) => (
        <section key={group.label}>
          <h3 className="sticky top-0 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">{group.label}</h3>
          <ul>
            {group.events.map((event) => {
              const href = event.htmlLink ?? event.hangoutLink;
              return (
                <li key={event.id} className="flex gap-3 border-b border-slate-100 px-3 py-2">
                  <p className={`w-16 shrink-0 text-xs ${event.outOfOffice ? "text-slate-400" : "text-slate-500"}`}>{eventClock(event)}</p>
                  <div className="min-w-0">
                    {href != null ? (
                      <a href={href} target="_blank" rel="noreferrer" className={`block truncate hover:underline ${event.outOfOffice ? "text-slate-500" : "font-medium text-slate-900"}`}>
                        {event.summary}
                      </a>
                    ) : (
                      <p className={`truncate ${event.outOfOffice ? "text-slate-500" : "font-medium text-slate-900"}`}>{event.summary}</p>
                    )}
                    {event.hangoutLink != null ? (
                      <a href={event.hangoutLink} target="_blank" rel="noreferrer" className="text-xs text-brand-700 hover:underline">
                        Join
                      </a>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ConnectChat({ label = "Connect Google Chat" }: { label?: string }) {
  const { showToast } = useHubToast();
  const connect = useMutation({
    mutationFn: () =>
      apiFetch<{ authorizeUrl: string }>("/api/google-chat/connect", {
        method: "POST",
        body: { returnTo: window.location.pathname, uiOrigin: window.location.origin },
      }),
    onSuccess: (result) => {
      window.location.assign(result.authorizeUrl);
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not start Google sign-in.",
      });
    },
  });
  return (
    <div className="p-3">
      <Button size="sm" isLoading={connect.isPending} onClick={() => connect.mutate()}>
        {label}
      </Button>
    </div>
  );
}

const RECENT_EMOJI_KEY = "hub-google-chat-recent-emoji";
const PICKER_EMOJIS = ["👍", "😀", "😂", "😍", "🎉", "✅", "❤️", "🙏", "👀", "🔥", "💯", "😢", "😮", "🤔", "👏", "🚀", "📌", "⚠️", "😄", "😅", "🙂", "😉", "😎", "🤝", "💪", "🎯", "📝", "💡", "⭐", "🚫", "⏰", "📎"];

function readRecentEmoji(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_EMOJI_KEY);
    const parsed = raw == null ? [] : JSON.parse(raw) as unknown;
    const saved = Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
    return [...saved, "👍", "😀", "🎉", "✅"].filter((emoji, index, list) => list.indexOf(emoji) === index).slice(0, 4);
  } catch {
    return ["👍", "😀", "🎉", "✅"];
  }
}

function rememberEmoji(emoji: string): void {
  const next = [emoji, ...readRecentEmoji().filter((item) => item !== emoji)].slice(0, 4);
  window.localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(next));
}

function mentionQuery(text: string): string | null {
  const match = /(?:^|\s)@([^\n@]*)$/.exec(text);
  return match == null ? null : (match[1] ?? "");
}

function looksLikeImage(contentType: string, contentName: string): boolean {
  return contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(contentName);
}

function ChatAttachmentView({ attachment }: { attachment: { name: string; contentType: string; contentName: string; previewUrl?: string } }) {
  const [url, setUrl] = useState<string | null>(attachment.previewUrl ?? null);
  const [blobType, setBlobType] = useState(attachment.contentType);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (attachment.previewUrl != null && attachment.previewUrl !== "") {
      setUrl(attachment.previewUrl);
      return;
    }
    let objectUrl = "";
    let cancelled = false;
    void apiFetchBlob(`/api/google-chat/media?name=${encodeURIComponent(attachment.name)}`).then((loaded) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(loaded.blob);
      setBlobType(loaded.contentType || loaded.blob.type || attachment.contentType);
      setUrl(objectUrl);
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl !== "") URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.name, attachment.previewUrl, attachment.contentType]);
  const image = looksLikeImage(blobType || attachment.contentType, attachment.contentName);
  if (image && url != null) {
    return (
      <>
        <button type="button" className="mt-1 block" onClick={() => setOpen(true)} aria-label={`Open ${attachment.contentName}`}>
          <img src={url} alt={attachment.contentName} className="max-h-48 max-w-full rounded" />
        </button>
        {open ? (
          <button type="button" className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-6" onClick={() => setOpen(false)}>
            <img src={url} alt={attachment.contentName} className="max-h-full max-w-full rounded" />
          </button>
        ) : null}
      </>
    );
  }
  if (url != null && !image) {
    return <a href={url} download={attachment.contentName} className="mt-1 block text-xs text-brand-700 hover:underline">{attachment.contentName}</a>;
  }
  return <p className="mt-1 text-xs text-slate-500">{failed ? `${attachment.contentName} could not be loaded` : attachment.contentName}</p>;
}

function conversationRows(
  messages: GoogleChatMessage[],
  openThread: string | null,
): Array<{ message: GoogleChatMessage; replyCount: number }> {
  if (openThread != null) return messages.map((message) => ({ message, replyCount: 0 }));
  const groups = new Map<string, GoogleChatMessage[]>();
  for (const message of messages) {
    const key = message.threadName ?? message.name;
    const group = groups.get(key) ?? [];
    group.push(message);
    groups.set(key, group);
  }
  return [...groups.values()]
    .map((group) => {
      const ordered = [...group].sort((left, right) => Date.parse(left.createTime ?? "") - Date.parse(right.createTime ?? ""));
      const root = ordered[0];
      return root == null ? null : { message: root, replyCount: Math.max(0, ordered.length - 1) };
    })
    .filter((row): row is { message: GoogleChatMessage; replyCount: number } => row != null)
    .sort((left, right) => Date.parse(left.message.createTime ?? "") - Date.parse(right.message.createTime ?? ""));
}

function ChatWindow({
  space,
  onCollapse,
  onClose,
  "data-chat-space": chatSpace,
}: {
  space: GoogleChatSpace;
  onCollapse: () => void;
  onClose: () => void;
  "data-chat-space"?: string;
}) {
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const threadRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [customWhen, setCustomWhen] = useState("");
  const [replyThread, setReplyThread] = useState<string | null>(null);
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; message: GoogleChatMessage } | null>(null);
  const [locked, setLocked] = useState(true);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null);
  const windowRef = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [recentEmoji, setRecentEmoji] = useState<string[]>(() => readRecentEmoji());
  const [mentions, setMentions] = useState<Array<{ name: string; displayName: string }>>([]);
  const [pendingMessages, setPendingMessages] = useState<GoogleChatMessage[]>([]);
  const [menuBox, setMenuBox] = useState<{ left: number; top: number } | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  async function addFiles(list: FileList | File[]): Promise<void> {
    const next: PendingFile[] = [];
    for (const file of Array.from(list).slice(0, 5)) {
      next.push(await readPendingFile(file));
    }
    setFiles((current) => [...current, ...next].slice(0, 5));
  }

  const members = useQuery({
    queryKey: ["google-chat", "members", space.name],
    queryFn: () => apiFetch<{ members: Array<{ name: string; displayName: string }> }>("/api/google-chat/members", {
      query: { space: space.name },
    }),
    staleTime: 60_000,
  });

  const messages = useQuery({
    queryKey: ["google-chat", "messages", space.name, openThread],
    queryFn: () =>
      apiFetch<{ messages: GoogleChatMessage[] }>("/api/google-chat/messages", {
        query: { space: space.name, thread: openThread ?? undefined },
      }),
    refetchInterval: 12_000,
  });

  useEffect(() => {
    const node = threadRef.current;
    if (node == null) return;
    node.scrollTop = node.scrollHeight;
  }, [messages.data, pendingMessages]);

  useLayoutEffect(() => {
    if (menu == null || menuRef.current == null) return;
    const rect = menuRef.current.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(margin, Math.min(menu.x, window.innerWidth - rect.width - margin));
    const top = Math.max(margin, Math.min(menu.y, window.innerHeight - rect.height - margin));
    setMenuBox((current) => (current?.left === left && current.top === top ? current : { left, top }));
  }, [menu, emojiPickerOpen]);

  useEffect(() => {
    if (menu == null) return;
    function closeIfOutside(event: PointerEvent) {
      if (menuRef.current?.contains(event.target as Node)) return;
      setMenu(null);
      setEmojiPickerOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setMenu(null);
      setEmojiPickerOpen(false);
    }
    document.addEventListener("pointerdown", closeIfOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menu]);

  useEffect(() => {
    if (messages.data == null) return;
    void apiFetch("/api/google-chat/read", { method: "POST", body: { space: space.name } }).catch(() => {
      // Opening the chat still clears the Hub badge when Google rejects the update.
    });
  }, [messages.data, space.name]);

  function reactWith(emoji: string): void {
    if (menu == null) return;
    const messageName = menu.message.name;
    rememberEmoji(emoji);
    setRecentEmoji(readRecentEmoji());
    setMenu(null);
    setEmojiPickerOpen(false);
    void apiFetch("/api/google-chat/reactions", { method: "POST", body: { message: messageName, emoji } }).then(() => {
      void queryClient.invalidateQueries({ queryKey: ["google-chat", "messages", space.name] });
    }).catch((error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Reaction was not added." });
    });
  }

  function queueLater(when: Date, text: string): void {
    if (text.trim() === "") return;
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      showToast({ tone: "error", message: "Pick a time in the future." });
      return;
    }
    const queued = readLater();
    queued.push({
      id: crypto.randomUUID(),
      space: space.name,
      text,
      thread: replyThread,
      sendAt: when.toISOString(),
    });
    window.localStorage.setItem(LATER_STORAGE_KEY, JSON.stringify(queued));
    setDraft("");
    setReplyThread(null);
    setScheduleOpen(false);
    setCustomWhen("");
    showToast({ tone: "success", message: "Message will send while this Hub tab stays open." });
  }

  const send = useMutation({
    mutationFn: async (payload: { text: string; files: PendingFile[]; thread: string | null; mentions: Array<{ name: string; displayName: string }> }) => {
      await apiFetch("/api/google-chat/messages", {
        method: "POST",
        body: {
          space: space.name,
          text: payload.text,
          thread: payload.thread,
          attachments: payload.files,
          annotations: payload.mentions.flatMap((mention) => {
            const token = `@${mention.displayName}`;
            const startIndex = payload.text.indexOf(token);
            if (startIndex < 0) return [];
            return [{
              type: "USER_MENTION",
              startIndex,
              length: token.length,
              userMention: { user: { name: mention.name, type: "HUMAN" }, type: "MENTION" },
            }];
          }),
        },
      });
    },
    onMutate: (payload) => {
      const localName = `local-${crypto.randomUUID()}`;
      const optimistic: GoogleChatMessage = {
        name: localName,
        text: payload.text,
        createTime: new Date().toISOString(),
        senderName: "You",
        senderAvatarUrl: null,
        senderType: "HUMAN",
        mine: true,
        threadName: payload.thread,
        reactions: [],
        attachments: payload.files.map((file, index) => ({
          name: `${localName}-${index}`,
          contentType: file.mimeType,
          contentName: file.filename,
          previewUrl: `data:${file.mimeType};base64,${file.dataBase64}`,
        })),
      };
      setPendingMessages((current) => [...current, optimistic]);
      setDraft("");
      setFiles([]);
      setMentions([]);
      setReplyThread(null);
      setScheduleOpen(false);
      if (composerRef.current != null) composerRef.current.style.height = "auto";
      return { localName, payload };
    },
    onSuccess: async (_data, _payload, context) => {
      await queryClient.invalidateQueries({ queryKey: ["google-chat", "messages", space.name] });
      setPendingMessages((current) => current.filter((message) => message.name !== context?.localName));
    },
    onError: (error, _payload, context) => {
      setPendingMessages((current) => current.filter((message) => message.name !== context?.localName));
      if (context?.payload != null) {
        setDraft(context.payload.text);
        setFiles(context.payload.files);
        setMentions(context.payload.mentions);
        setReplyThread(context.payload.thread);
      }
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Message was not sent.",
      });
    },
  });

  const saveEdit = useMutation({
    mutationFn: () =>
      apiFetch("/api/google-chat/messages", {
        method: "PATCH",
        body: { message: editing, text: editText.trim() },
      }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["google-chat", "messages", space.name] });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Message was not edited.",
      });
    },
  });

  const spaceId = space.name.startsWith("spaces/") ? space.name.slice("spaces/".length) : space.name;
  const googleChatUrl = `https://chat.google.com/room/${spaceId}`;

  return (
    <section
      ref={windowRef}
      data-chat-space={chatSpace}
      className="flex h-[28rem] w-96 shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (event.dataTransfer.files.length > 0) void addFiles(event.dataTransfer.files);
      }}
      style={dragPos != null ? { position: "fixed", left: dragPos.x, top: dragPos.y, zIndex: 95 } : undefined}
    >
      <header
        className={`flex items-center justify-between gap-2 border-b border-slate-200 px-2 py-2 ${locked ? "" : "cursor-move"}`}
        onPointerDown={(event) => {
          if (locked || windowRef.current == null) return;
          const target = event.target as HTMLElement;
          if (target.closest("button")) return;
          const rect = windowRef.current.getBoundingClientRect();
          dragOffset.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (dragOffset.current == null) return;
          setDragPos({
            x: event.clientX - dragOffset.current.dx,
            y: event.clientY - dragOffset.current.dy,
          });
        }}
        onPointerUp={() => {
          dragOffset.current = null;
        }}
      >
        <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-900">
          <ConversationIcon name={space.displayName} type={space.type} url={space.avatarUrl} />
          <span className="truncate">{space.displayName}</span>
        </h2>
        <div className="flex shrink-0 items-center gap-1 text-slate-600">
          <button
            type="button"
            aria-label={locked ? "Unlock to drag" : "Lock in the dock"}
            className="rounded p-1 hover:bg-slate-100"
            onClick={() => {
              if (locked && windowRef.current != null) {
                const rect = windowRef.current.getBoundingClientRect();
                setDragPos({ x: rect.left, y: rect.top });
                setLocked(false);
                return;
              }
              setLocked(true);
              setDragPos(null);
            }}
          >
            {locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
          </button>
          <a
            href={googleChatUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Open in Google Chat"
            className="rounded p-1 hover:bg-slate-100"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button type="button" aria-label="Minimize" className="rounded p-1 hover:bg-slate-100" onClick={onCollapse}>
            <Minus className="h-4 w-4" />
          </button>
          <button type="button" aria-label="Close" className="rounded p-1 hover:bg-slate-100" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>
      {openThread != null ? (
        <p className="flex items-center justify-between border-b border-slate-200 px-2 py-1 text-xs text-slate-600">
          Thread
          <button type="button" className="text-brand-700 hover:underline" onClick={() => { setOpenThread(null); setReplyThread(null); }}>
            Back to conversation
          </button>
        </p>
      ) : null}
      <div ref={threadRef} className="min-h-0 flex-1 space-y-2 overflow-x-hidden overflow-y-auto p-2">
        {conversationRows(
          [
            ...(messages.data?.messages ?? []),
            ...pendingMessages.filter((pending) => !(messages.data?.messages ?? []).some((item) => item.mine && item.text === pending.text && Date.parse(item.createTime ?? "") >= Date.parse(pending.createTime ?? "") - 5000)),
          ],
          openThread,
        ).map(({ message, replyCount }) => (
          <article
            key={message.name}
            className="group relative flex min-w-0 gap-2 text-sm"
            onContextMenu={(event) => {
              event.preventDefault();
              setMenu({ x: event.clientX, y: event.clientY, message });
            }}
          >
            {message.mine ? (
              <div className="absolute right-0 top-0 z-10 hidden rounded border border-slate-200 bg-white px-1 py-0.5 text-xs shadow group-hover:block">
                <button type="button" onClick={() => { setEditing(message.name); setEditText(message.text); }}>Edit</button>
              </div>
            ) : null}
            <SenderAvatar name={message.senderName} url={message.senderAvatarUrl} />
            <div className="min-w-0">
              <p className="text-slate-800">
                <span className="font-medium">{message.senderName}</span>
                <span className="ml-2 text-xs text-slate-500">{formatChatTime(message.createTime)}</span>
              </p>
              {editing === message.name ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveEdit.mutate();
                  }}
                >
                  <input
                    className="w-full rounded border border-slate-300 px-1 text-sm"
                    value={editText}
                    onChange={(event) => setEditText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        setEditing(null);
                      }
                    }}
                    aria-label="Edit message"
                    autoFocus
                  />
                </form>
              ) : (
                <p className="whitespace-pre-wrap break-words text-slate-700 [overflow-wrap:anywhere]">{message.text}</p>
              )}
              {(message.attachments ?? []).map((attachment) => (
                <ChatAttachmentView key={attachment.name} attachment={attachment} />
              ))}
              {message.reactions.length > 0 ? (
                  <p className="mt-1 flex flex-wrap gap-1 text-xs">
                    {message.reactions.map((reaction) => (
                      <HoverHintContent
                        key={reaction.emoji}
                        cursor="default"
                        hoverHint={<span>{reaction.people.join(", ")} reacted with {reaction.emoji}</span>}
                      >
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5">
                          {reaction.emoji}{reaction.people.length > 1 ? ` ${reaction.people.length}` : ""}
                        </span>
                      </HoverHintContent>
                    ))}
                  </p>
              ) : null}
              {replyCount > 0 && message.threadName != null ? (
                <button
                  type="button"
                  className="mt-1 text-xs text-brand-700 hover:underline"
                  onClick={() => setOpenThread(message.threadName)}
                >
                  {replyCount === 1 ? "1 reply" : `${replyCount} replies`}
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>
      {menu != null ? (
        <div
          ref={menuRef}
          className="fixed z-[120] max-h-[calc(100vh-16px)] w-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
          style={{ left: menuBox?.left ?? menu.x, top: menuBox?.top ?? menu.y }}
        >
          {menu.message.mine ? (
            <button type="button" className="block w-full px-3 py-1 text-left hover:bg-slate-100" onClick={() => { setEditing(menu.message.name); setEditText(menu.message.text); setMenu(null); }}>Edit</button>
          ) : null}
          <button
            type="button"
            className="block w-full px-3 py-1 text-left hover:bg-slate-100"
            disabled={menu.message.threadName == null}
            onClick={() => {
              setReplyThread(menu.message.threadName);
              setOpenThread(menu.message.threadName);
              setMenu(null);
            }}
          >
            Reply in thread
          </button>
          {menu.message.threadName != null && (messages.data?.messages ?? []).filter((item) => item.threadName === menu.message.threadName).length > 1 ? (
            <button
              type="button"
              className="block w-full px-3 py-1 text-left hover:bg-slate-100"
              onClick={() => { setOpenThread(menu.message.threadName); setMenu(null); }}
            >
              Open thread
            </button>
          ) : null}
          <p className="px-3 pt-1 text-xs text-slate-500">React</p>
          <div className="grid grid-cols-4 gap-1 px-2 py-1">
            {recentEmoji.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="rounded px-1 py-0.5 text-base hover:bg-slate-100"
                aria-label={`React with ${emoji}`}
                onClick={() => reactWith(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="block w-full px-3 py-1 text-left text-slate-600 hover:bg-slate-100"
            onClick={() => setEmojiPickerOpen((value) => !value)}
          >
            {emojiPickerOpen ? "Hide emoji" : "More emoji"}
          </button>
          {emojiPickerOpen ? (
            <div className="grid max-h-40 grid-cols-8 gap-0.5 overflow-y-auto px-2 pb-2">
              {PICKER_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded px-0.5 py-0.5 text-base hover:bg-slate-100"
                  aria-label={`React with ${emoji}`}
                  onClick={() => reactWith(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {replyThread != null ? (
        <p className="px-2 text-xs text-slate-500">
          Replying in a thread. <button type="button" onClick={() => setReplyThread(null)}>Cancel</button>
        </p>
      ) : null}
      <form
        className="border-t border-slate-200 p-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (draft.trim() === "" && files.length === 0) return;
          send.mutate({ text: draft.trim(), files, thread: replyThread, mentions });
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.files;
          if (pasted.length > 0) {
            event.preventDefault();
            void addFiles(pasted);
          }
        }}
      >
        {files.length > 0 ? (
          <ul className="mb-1 flex flex-wrap gap-1 text-xs text-slate-600">
            {files.map((file) => (
              <li key={`${file.filename}-${file.dataBase64.slice(0, 24)}`} className="flex items-center gap-1 rounded bg-slate-100 px-1 py-0.5">
                {file.mimeType.startsWith("image/") ? (
                  <img src={`data:${file.mimeType};base64,${file.dataBase64}`} alt={file.filename} className="h-10 w-10 rounded object-cover" />
                ) : (
                  file.filename
                )}
                <button type="button" className="ml-1" onClick={() => setFiles((current) => current.filter((item) => item !== file))}>
                  x
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {(() => {
          const query = mentionQuery(draft);
          const choices = query == null
            ? []
            : (members.data?.members ?? []).filter((member) => member.displayName.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
          return choices.length > 0 ? (
            <ul className="mb-1 max-h-32 overflow-y-auto rounded border border-slate-200 bg-white text-sm">
              {choices.map((member) => (
                <li key={member.name}>
                  <button
                    type="button"
                    className="block w-full px-2 py-1 text-left hover:bg-slate-100"
                    onClick={() => {
                      setDraft((current) => current.replace(/@([^\n@]*)$/, `@${member.displayName} `));
                      setMentions((current) => current.some((item) => item.name === member.name) ? current : [...current, member]);
                    }}
                  >
                    {member.displayName}
                  </button>
                </li>
              ))}
            </ul>
          ) : null;
        })()}
        <div className="flex items-end gap-2">
          <textarea
            ref={composerRef}
            rows={1}
            className="max-h-32 min-h-8 min-w-0 flex-1 resize-none overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-slate-300 px-2 py-1 text-sm [overflow-wrap:anywhere]"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              event.target.style.height = "auto";
              event.target.style.height = `${event.target.scrollHeight}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            aria-label="Message"
          />
          <div className="relative flex">
            <Button size="sm" type="submit" isLoading={send.isPending} disabled={draft.trim() === "" && files.length === 0} className="rounded-r-none">
              Send
            </Button>
            <button
              type="button"
              aria-label="Schedule send"
              className="rounded-r-md border border-l-0 border-brand-600 bg-brand-600 px-1 text-white"
              onClick={() => setScheduleOpen((value) => !value)}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {scheduleOpen ? (
              <div className="absolute bottom-10 right-0 z-10 w-56 rounded-md border border-slate-200 bg-white p-2 text-sm shadow-lg">
                <button
                  type="button"
                  className="block w-full rounded px-2 py-1 text-left hover:bg-slate-100"
                  onClick={() => queueLater(new Date(Date.now() + 60 * 60 * 1000), draft.trim())}
                >
                  In 1 hour
                </button>
                <button
                  type="button"
                  className="block w-full rounded px-2 py-1 text-left hover:bg-slate-100"
                  onClick={() => {
                    const when = new Date();
                    when.setDate(when.getDate() + 1);
                    when.setHours(8, 0, 0, 0);
                    queueLater(when, draft.trim());
                  }}
                >
                  Tomorrow at 8:00 AM
                </button>
                <label className="mt-2 block text-xs text-slate-500">
                  Custom time
                  <input
                    type="datetime-local"
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-900"
                    value={customWhen}
                    onChange={(event) => setCustomWhen(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="mt-2 w-full rounded bg-slate-100 px-2 py-1"
                  onClick={() => queueLater(new Date(customWhen), draft.trim())}
                >
                  Schedule
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </form>
    </section>
  );
}
